import { type VFC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { TriggerButton } from "widgets";

import { type InputTableQuerySpec } from "@/graphql.ts";
import { createTempGroup } from "@/redux/modules/saved-group.ts";
import { type ITableData, getTableQuerySpecWithSelection } from "@/redux/modules/table-data.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE } from "@/settings/settings.ts";

interface ISampleChartButtonProps {
  activeTable: ITableData;
}

/**
 * Button that allows to push a selection of samples (or full group) to the analytical viewer.
 *
 */
const SampleChartButton: VFC<ISampleChartButtonProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();

  const chartSampleSelection = useCallback(() => {
    const table: ITableData = props.activeTable;

    if (table.tableQuerySpec.idType === DATATABLE_ID_TYPE.GROUP) {
      navigate(`/visualizer/data/group/${table.tableQuerySpec.id}`);
      return;
    } else {
      // create a group in the saved-group slice and pass the ID to chartSampleGroup

      // build a temporary group first which will be used by the visualization
      const selectedIds: number[] =
        table.selectionModel.selectedIds.length > 0 ? (table.selectionModel.selectedIds as number[]) : table.rowIds;

      const tableQuerySpec: InputTableQuerySpec = getTableQuerySpecWithSelection(table, selectedIds);

      return dispatch(createTempGroup(tableQuerySpec)).then((groupId: number) =>
        navigate(`/visualizer/data/group/${groupId}`),
      );
    }
  }, [props.activeTable]);

  const enabled: boolean = props.activeTable.rowIds && props.activeTable.rowIds.length > 0;

  return (
    <TriggerButton
      icon={"bar_chart"}
      label={"Chart"}
      key={"sample-chart-btn-header"}
      tooltip={enabled ? "Visualize data for the selected samples" : "Nothing to visualize"}
      onPress={chartSampleSelection}
      showLabel
    />
  );
};

export default SampleChartButton;
